import { useEffect, useState, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Loader2, Search, Upload, Save, CheckCircle2, X, FileSpreadsheet, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

/**
 * CompletarCodigosFaltantes — herramienta admin para llenar el "Código Real"
 * de productos activos que quedaron sin código. Dos modos:
 *   A. Tabla inline: mostrar todos los faltantes, editar con teclado (Enter/Tab).
 *   B. Upload Excel/CSV: subir un archivo SKU|Código, preview, confirmar.
 * Ambos flujos convergen en POST /productos/_/bulk-codigos.
 */
export default function CompletarCodigosFaltantes() {
  const [loading, setLoading] = useState(true);
  const [productos, setProductos] = useState([]);
  const [codigos, setCodigos] = useState({}); // producto_id → codigo (input state)
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [zoomImg, setZoomImg] = useState(null);
  // Estado del upload Excel
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/productos/_/sin-codigo");
      setProductos(data.productos || []);
      setCodigos({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar la lista");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtrados = useMemo(() => {
    if (!q.trim()) return productos;
    const s = q.trim().toLowerCase();
    return productos.filter((p) =>
      (p.nombre || "").toLowerCase().includes(s) ||
      (p.sku || "").toLowerCase().includes(s) ||
      (p.familia_key || "").toLowerCase().includes(s)
    );
  }, [productos, q]);

  const conCambios = useMemo(
    () => Object.entries(codigos).filter(([, v]) => (v || "").trim().length > 0),
    [codigos]
  );

  const guardar = async () => {
    if (conCambios.length === 0) {
      toast.info("No hay códigos escritos para guardar");
      return;
    }
    setSaving(true);
    try {
      const items = conCambios.map(([producto_id, codigo]) => ({ producto_id, codigo: codigo.trim() }));
      const { data } = await api.post("/productos/_/bulk-codigos", { items, motivo: "Completado manual" });
      toast.success(`${data.afectados} códigos guardados`);
      if (data.duplicados_en_batch?.length) {
        toast.warning(`⚠️ ${data.duplicados_en_batch.length} códigos duplicados en el batch — revisa`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  // --- UPLOAD EXCEL FLOW ---
  const onUploadFile = async (evt) => {
    const file = evt.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/productos/_/bulk-codigos-excel", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo leer el archivo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const confirmarUpload = async () => {
    if (!uploadPreview?.items?.length) return;
    setSaving(true);
    try {
      const items = uploadPreview.items.map((it) => ({ producto_id: it.producto_id, codigo: it.codigo_nuevo }));
      const { data } = await api.post("/productos/_/bulk-codigos", { items, motivo: "Upload Excel/CSV" });
      toast.success(`${data.afectados} códigos aplicados desde el archivo`);
      setUploadPreview(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al aplicar");
    } finally { setSaving(false); }
  };

  const IMG_URL = (path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
  };

  return (
    <Card className="p-6 border-2 border-teal-200 bg-teal-50/30">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 className="font-display text-xl flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-teal-700" />
            Completar Códigos Faltantes
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Productos activos sin Código Real. Escribe el código y guarda todos de una vez, o sube un Excel/CSV.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={onUploadFile}
            className="hidden"
            data-testid="upload-excel-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            variant="outline"
            disabled={uploading}
            data-testid="upload-excel-btn"
            title="Subir Excel o CSV con columnas SKU y Código"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Subir Excel/CSV
          </Button>
          <Button
            onClick={guardar}
            disabled={saving || conCambios.length === 0}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="save-codigos-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar {conCambios.length > 0 ? `(${conCambios.length})` : ""}
          </Button>
        </div>
      </div>

      {/* Buscador */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nombre, SKU o familia..."
          className="pl-10"
          data-testid="search-faltantes"
        />
      </div>

      <div className="text-xs text-muted-foreground mb-2 flex items-center justify-between">
        <span>
          <b className="text-teal-700">{filtrados.length}</b> productos activos sin código
          {q && ` (filtrado de ${productos.length})`}
        </span>
        {conCambios.length > 0 && (
          <span className="text-emerald-700 font-semibold">
            ✓ {conCambios.length} código{conCambios.length !== 1 ? "s" : ""} listo{conCambios.length !== 1 ? "s" : ""} para guardar
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : filtrados.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
          <p className="font-semibold">¡Todos los productos tienen código real!</p>
        </div>
      ) : (
        <div className="border rounded-md overflow-hidden">
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card border-b z-10">
                <tr className="text-left">
                  <th className="px-2 py-2 w-16">Foto</th>
                  <th className="px-3 py-2">Nombre</th>
                  <th className="px-3 py-2 w-32">SKU</th>
                  <th className="px-3 py-2 w-16 text-center">Stock</th>
                  <th className="px-3 py-2 w-56">Código real</th>
                </tr>
              </thead>
              <tbody>
                {filtrados.map((p, idx) => (
                  <tr key={p.id} className="border-b hover:bg-teal-50/40" data-testid={`faltante-row-${p.sku}`}>
                    <td className="px-2 py-1.5">
                      {p.imagen_path ? (
                        <button
                          onClick={() => setZoomImg({ src: IMG_URL(p.imagen_path), alt: p.nombre })}
                          className="w-11 h-11 rounded-md overflow-hidden border border-border hover:border-teal-500 transition"
                          data-testid={`zoom-img-${p.sku}`}
                          title="Click para agrandar"
                        >
                          <img src={IMG_URL(p.imagen_path)} alt={p.nombre} className="w-full h-full object-cover" />
                        </button>
                      ) : (
                        <div className="w-11 h-11 rounded-md border border-dashed border-border flex items-center justify-center text-muted-foreground">
                          <ImageIcon className="w-4 h-4" />
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-1.5 truncate max-w-[380px]" title={p.nombre}>
                      <div className="font-medium">{p.nombre}</div>
                      {p.color && <div className="text-xs text-muted-foreground">{p.color}</div>}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">{p.sku}</td>
                    <td className="px-3 py-1.5 text-center text-xs tabular-nums">{p.stock_actual ?? 0}</td>
                    <td className="px-3 py-1.5">
                      <Input
                        value={codigos[p.id] || ""}
                        onChange={(e) => setCodigos({ ...codigos, [p.id]: e.target.value.toUpperCase() })}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            // Focus el siguiente input
                            const inputs = Array.from(document.querySelectorAll('[data-testid^="input-codigo-"]'));
                            const currentIdx = inputs.indexOf(e.target);
                            const next = inputs[currentIdx + 1];
                            if (next) next.focus();
                          }
                        }}
                        placeholder="Ej: EF26-149"
                        className="h-8 font-mono text-xs"
                        data-testid={`input-codigo-${p.sku}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal zoom imagen */}
      <Dialog open={!!zoomImg} onOpenChange={(v) => { if (!v) setZoomImg(null); }}>
        <DialogContent className="max-w-2xl p-0 bg-transparent border-0 shadow-none">
          {zoomImg && (
            <div className="relative">
              <img src={zoomImg.src} alt={zoomImg.alt} className="w-full h-auto rounded-lg" />
              <button
                onClick={() => setZoomImg(null)}
                className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full"
                data-testid="close-zoom"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="absolute bottom-2 left-2 right-2 bg-black/60 text-white text-sm p-2 rounded">
                {zoomImg.alt}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal preview upload */}
      <Dialog open={!!uploadPreview} onOpenChange={(v) => { if (!v) setUploadPreview(null); }}>
        <DialogContent className="max-w-4xl max-h-[92vh] flex flex-col" data-testid="upload-preview-modal">
          <div className="flex items-center justify-between border-b pb-3 mb-3">
            <div>
              <h4 className="font-display text-lg">Preview del archivo</h4>
              <p className="text-xs text-muted-foreground">
                {uploadPreview?.matches} de {uploadPreview?.pares_validos} pares hacen match con productos activos
              </p>
            </div>
            <Button
              onClick={confirmarUpload}
              disabled={saving || !uploadPreview?.items?.length}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="confirm-upload-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Aplicar {uploadPreview?.items?.length} códigos
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {uploadPreview?.sku_no_encontrados?.length > 0 && (
              <div className="mb-3 p-3 bg-amber-50 border border-amber-300 rounded">
                <div className="text-xs font-bold text-amber-800 uppercase mb-1">
                  {uploadPreview.sku_no_encontrados.length} SKUs no encontrados (se ignorarán)
                </div>
                <div className="text-xs font-mono text-amber-700 max-h-20 overflow-y-auto">
                  {uploadPreview.sku_no_encontrados.join(", ")}
                </div>
              </div>
            )}
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-card border-b">
                <tr className="text-left text-muted-foreground">
                  <th className="px-3 py-1.5">SKU</th>
                  <th className="px-3 py-1.5">Nombre</th>
                  <th className="px-3 py-1.5">Código actual</th>
                  <th className="px-3 py-1.5">Código nuevo</th>
                </tr>
              </thead>
              <tbody>
                {uploadPreview?.items?.map((it) => {
                  const cambio = it.codigo_anterior && it.codigo_anterior !== it.codigo_nuevo;
                  return (
                    <tr key={it.producto_id} className={`border-b ${cambio ? "bg-amber-50" : ""}`}>
                      <td className="px-3 py-1.5 font-mono">{it.sku}</td>
                      <td className="px-3 py-1.5 truncate max-w-[260px]" title={it.nombre}>{it.nombre}</td>
                      <td className={`px-3 py-1.5 font-mono ${cambio ? "text-amber-700 font-bold" : "text-muted-foreground"}`}>
                        {it.codigo_anterior || "—"}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-emerald-700 font-bold">{it.codigo_nuevo}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
